import * as people from "@/api/people";
import {
  REDEEM_PHONE,
  REDEEM_EMAIL,
  REDEEM_EMAIL_AND_PHONE,
} from "@/views/search/constants";
import router from "@/router";
import { getIsAccessDisabled } from "@/utils/permissions";
import { contactDetailsPayload, isContactDetailsActive } from "@/utils/contactDetails";
import {
  withoutUnsupportedJobLocationOperators,
} from "@/views/search/search-common/location-filters";

export const getInitialFilterState = () => ({
  redeem_queries: [],
  search_term: "",
  people_groups: [],
  people_name: "",
  people_titles: [],
  exclude_people_titles: [],
  departments: [],
  exclude_departments: [],
  department_functions: [],
  exclude_department_functions: [],
  management_levels: [],
  contact_details: { types: [], exclude_without: false },
  employees: [],
  verticals: [],
  vertical_categories: [],
  vertical_sub_categories: [],
  people_locations: new Map(),
  exclude_people_locations: new Map(),
  company_locations: new Map(),
  company_exclude_locations: new Map(),
  companies: new Map(),
  technologies: [],
  categories: [],
  sequences: [],
  search_modes: ["real_time"],
  page: 1,
  notReset: false,
  per_page: parseInt(process.env.VUE_APP_PER_PAGE),

  social_media: [],
  founded_dates: [],
  revenues: [],
  job_locations: new Map(),
  job_exclude_locations: new Map(),
  job_posted_dates: [],
  job_titles: [],
  exclude_job_titles: [],
  keywords: [],
  news_categories: [],
  news_published_dates: [],
  advertisement_target_locations: new Map(),
  advertisement_exclude_target_locations: new Map(),
  advertisement_search_terms: [],
  advertisement_start_dates: [],
  advertisement_end_dates: [],

  places: [],
  exclude_places: [],

  filter_conditions: [
    {
      key: "technologies",
      operator: "or",
    },
    {
      key: "categories",
      operator: "or",
    },
    {
      key: "verticals",
      operator: "or",
    },
    {
      key: "vertical_categories",
      operator: "or",
    },
    {
      key: "vertical_sub_categories",
      operator: "or",
    },
    {
      key: "keywords",
      operator: "or",
    },
    {
      key: "places",
      operator: "or",
    },
    {
      key: "exclude_places",
      operator: "or",
    },
    {
      key: "social_media",
      operator: "or",
    },
    {
      key: "contact_details",
      operator: "or",
    },
  ],
});

const state = {
  selected: getInitialFilterState(),

  checkedList: [],
  isLoading: false,
  isShow: true,
  lists: [],
  total: {},
  peopleExportFields: [],
  peopleSelectedModule: "",
  multipleSelectedListIds: [],
  savedList: {},
  sequenceList: {},
  isRealTimeMode: true,
  showLimitPopup: false,
  searchAbortController: null,
  currentRequestId: 0,

  people_search_list_access_code: null,
};

const getters = {
  apiPayload: (state, getters, rootState) => {
    const { selected } = state;

    const payload = {
      ...selected,
      profile_id: rootState.user.currentId.profile_id,
      companies: [...selected.companies.keys()],
      people_groups: selected?.people_groups?.map((i) => i.value),
      departments: selected.departments?.map((i) => i.value),
      exclude_departments: selected.exclude_departments?.map((i) => i.value),
      department_functions: selected.department_functions?.map((i) => i.value),
      exclude_department_functions: selected.exclude_department_functions?.map(
        (i) => i.value,
      ),
      management_levels: selected.management_levels?.map((i) => i.value),
      verticals: selected.verticals?.map((i) => i.value),
      vertical_categories: selected.vertical_categories?.map((i) => i.value),
      vertical_sub_categories: selected.vertical_sub_categories?.map(
        (i) => i.value,
      ),
      people_locations: [...selected.people_locations.keys()],
      exclude_people_locations: [...selected.exclude_people_locations.keys()],
      company_locations: [...selected.company_locations.keys()],
      company_exclude_locations: [...selected.company_exclude_locations.keys()],
      technologies: selected.technologies?.map((i) => i.value),
      categories: selected.categories?.map((i) => i.value),
      sequences: selected.sequences?.map((i) => i.value),
      page: selected.notReset ? selected.page : 1,

      job_locations: [...selected.job_locations.keys()],
      job_exclude_locations: [...selected.job_exclude_locations.keys()],
      news_categories: selected.news_categories?.map((item) => item.value),
      social_media: selected.social_media?.map((item) => item.value),
      advertisement_target_locations: [
        ...selected.advertisement_target_locations.keys(),
      ],
      advertisement_exclude_target_locations: [
        ...selected.advertisement_exclude_target_locations.keys(),
      ],
      places: selected.places,
      exclude_places: selected.exclude_places,
      // API search uses company-prefixed keys; saved searches keep the plain keys.
      company_places: selected.places,
      company_exclude_places: selected.exclude_places,
      filter_conditions: withoutUnsupportedJobLocationOperators(
        selected.filter_conditions,
      ),
      contact_details: contactDetailsPayload(selected.contact_details),
      // internal: {
      //   error_code: 42903,
      // }
    };

    delete payload.notReset;
    if (!payload.contact_details) delete payload.contact_details;
    return payload;
  },
};

const mutations = {
  set_people_search_list_access_code(state, payload) {
    state.people_search_list_access_code = payload;
  },
  SET_FILTERS_FROM_PAYLOAD(state, payload) {
    state.selected = {
      ...getInitialFilterState(),
      ...state.selected,
      ...payload,
    };
  },

  UPDATE_FILTER(state, { dataKey, value }) {
    if (state.selected.hasOwnProperty(dataKey)) {
      state.selected[dataKey] = value;
      state.selected.page = 1;
      state.selected = { ...state.selected };
    }
  },

  UPSERT_TO_MAP_FILTER(state, { dataKey, item }) {
    if (state.selected[dataKey] instanceof Map) {
      state.selected[dataKey].set(item.value, item);
      state.selected = { ...state.selected };
    }
  },

  ADD_TO_ARRAY_FILTER(state, { dataKey, item }) {
    if (Array.isArray(state.selected[dataKey])) {
      const list = state.selected[dataKey];
      const exists = list.some((i) => i.value === item.value);
      if (!exists) {
        list.push(item);
        state.selected = { ...state.selected };
      }
    }
  },

  REMOVE_FROM_MAP_FILTER(state, { dataKey, itemValue }) {
    if (state.selected[dataKey] instanceof Map) {
      if (state.selected[dataKey].delete(itemValue)) {
        state.selected = { ...state.selected };
      }
    }
  },

  REMOVE_FROM_ARRAY_FILTER(state, { dataKey, itemValue }) {
    if (Array.isArray(state.selected[dataKey])) {
      const list = state.selected[dataKey];
      const index = list.findIndex((i) => i.value === itemValue);
      if (index > -1) {
        list.splice(index, 1);
        state.selected = { ...state.selected };
      }
    }
  },

  CLEAR_COMPOSITE_FILTER(state, dataKey) {
    if (dataKey === "industry") {
      state.selected.vertical_categories = [];
      state.selected.vertical_sub_categories = [];
    } else if (dataKey === "technology") {
      state.selected.technologies = [];
      state.selected.categories = [];
    } else if (dataKey === "companyAndPeopleLocations") {
      state.selected.company_locations = new Map();
      state.selected.company_exclude_locations = new Map();
      state.selected.people_locations = new Map();
      state.selected.exclude_people_locations = new Map();
    } else if (dataKey === "news") {
      state.selected.news_categories = [];
      state.selected.news_published_dates = [];
    } else if (dataKey === "jobs") {
      state.selected.job_locations = new Map();
      state.selected.job_exclude_locations = new Map();
      state.selected.job_posted_dates = [];
      state.selected.job_titles = [];
      state.selected.exclude_job_titles = [];
    } else if (dataKey === "advertisement") {
      state.selected.advertisement_target_locations = new Map();
      state.selected.advertisement_exclude_target_locations = new Map();
      state.selected.advertisement_search_terms = [];
      state.selected.advertisement_start_dates = [];
      state.selected.advertisement_end_dates = [];
    } else if (dataKey === "places") {
      state.selected.places = [];
      state.selected.exclude_places = [];
    }
    state.selected = { ...state.selected };
  },

  RESET_ALL_FILTERS(state) {
    state.selected = getInitialFilterState();
  },

  /*---------------- 原有的、与筛选器无关的 Mutations 保持不变 -----------------*/
  setPeopleSelectedModule(state, payload) {
    state.peopleSelectedModule = payload;
  },
  setCollapse(state) {
    state.isShow = !state.isShow;
  },
  setLoading(state, val) {
    state.isLoading = val;
  },
  setCheckedList(state, payload) {
    state.checkedList = payload;
  },
  setSavedList(state, payload) {
    state.savedList = payload;
  },
  setSequenceList(state, payload) {
    state.sequenceList = payload;
  },
  setMultiAddSavedList(state, addedGroupInfo) {
    const ids = state.checkedList.map((item) => item.people_search_id);
    state.lists = state.lists.map((item) => {
      if (ids.includes(item.people_search_id)) {
        if (item.saved_lists) {
          const groupIds = item.saved_lists.map((group) => group.group_id);
          return groupIds.includes(addedGroupInfo.group_id)
            ? item
            : {
                ...item,
                saved_lists: [...item.saved_lists, addedGroupInfo],
              };
        } else {
          return {
            ...item,
            saved_lists: [addedGroupInfo],
          };
        }
      } else {
        return item;
      }
    });
  },
  setRowAddSavedList(state, payload) {
    const { people_search_id, group } = payload;
    state.lists = state.lists.map((item) => {
      if (item.people_search_id === people_search_id) {
        if (item.saved_lists) {
          const groupIds = item.saved_lists.map(
            (groupItem) => groupItem.group_id,
          );
          return groupIds.includes(group.group_id)
            ? item
            : {
                ...item,
                saved_lists: [...item.saved_lists, group],
              };
        } else {
          return {
            ...item,
            saved_lists: [group],
          };
        }
      } else {
        return item;
      }
    });
  },
  setMultiDeleteSavedList(state, groupInfo) {
    const savedList = structuredClone(state.savedList);
    delete savedList[groupInfo.groupId];
    state.savedList = savedList;
    const ids = state.checkedList.map((item) => item.people_search_id);
    state.lists = state.lists.map((item) => {
      return ids.includes(item.people_search_id)
        ? {
            ...item,
            saved_lists: item.saved_lists.filter(
              (groupItem) => groupItem.group_id !== groupInfo.groupId,
            ),
          }
        : item;
    });
  },
  setRowDeleteSavedList(state, payload) {
    const { people_search_id, group } = payload;
    state.lists = state.lists.map((item) => {
      if (item.people_search_id === people_search_id) {
        return {
          ...item,
          saved_lists: item.saved_lists.filter(
            (groupItem) => groupItem.group_id !== group.group_id,
          ),
        };
      } else {
        return item;
      }
    });
  },
  setSelected(state, val) {
    if (val.recall) {
      val = val.info;
    }
    const { notReset, ...rest } = val;
    let temp = {
      profile_id: this.state.user.currentId.profile_id,
      ...state.selected,
      ...rest,
      page: val.notReset ? val.page : 1,
    };
    state.selected = temp;
    const { profile_id, ...restParams } = temp;
  },
  setSearchPeopleList(state, payload) {
    if (payload.data) {
      payload.data.peoples &&
        (state.lists = payload.data.peoples?.map((item) => ({
          ...item,
          loading: false,
        })));
      payload.data.pagination && (state.total = payload.data.pagination);
    }
  },
  setPeopleExportFields(state, payload) {
    state.peopleExportFields = payload;
  },
  setMultipleSelectedListIds(state, payload) {
    state.multipleSelectedListIds = payload;
  },
  setRealTimeMode(state, payload) {
    state.isRealTimeMode = payload;
  },
  setLimitPopup(state, payload) {
    state.showLimitPopup = payload;
  },
  setSearchAbortController(state, controller) {
    state.searchAbortController = controller;
  },
  setRequestId(state, payload) {
    state.currentRequestId = payload;
  },
  updateContact(state, { people_search_id, data, contactType }) {
    const index = state.lists.findIndex(
      (p) => p.people_search_id === people_search_id,
    );
    if (index !== -1) {
      const contacts = [
        ...(state.lists[index].contacts || []).filter(
          (c) =>
            !c.type.includes(contactType === REDEEM_EMAIL ? "email" : "phone"),
        ),
        ...data.map((e) => ({ ...e, is_redeemed: true })),
      ];
      const typeParams =
        contactType === REDEEM_EMAIL
          ? { redeemEmail: true }
          : { redeemPhone: true };
      state.lists = [
        ...state.lists.slice(0, index),
        {
          ...state.lists[index],
          contacts,
          ...typeParams,
        },
        ...state.lists.slice(index + 1),
      ];
    }
  },
  updateMultipleContacts(state, { data, type }) {
    let typeList = [];
    if (type === REDEEM_EMAIL) {
      typeList = ["phone"];
    } else if (type === REDEEM_PHONE) {
      typeList = ["email-personal", "email-work", "email-generic"];
    }

    state.lists = state.lists.map((item) => {
      const matchingData = data.find(
        (child) => item.people_search_id === child.people_search_id,
      );

      if (matchingData) {
        const { emails, phones } = matchingData;
        const updatedContacts = [];

        if (type === REDEEM_EMAIL || type === REDEEM_EMAIL_AND_PHONE) {
          updatedContacts.push(
            ...(emails
              ? emails.map((email) => ({
                  ...email,
                  is_redeemed: true,
                }))
              : []),
          );
        }

        if (type === REDEEM_PHONE || type === REDEEM_EMAIL_AND_PHONE) {
          updatedContacts.push(
            ...(phones
              ? phones.map((phone) => ({
                  ...phone,
                  is_redeemed: true,
                }))
              : []),
          );
        }

        return {
          ...item,
          contacts: [
            ...(item.contacts ?? []).filter((contactItem) =>
              typeList.includes(contactItem.type),
            ),
            ...updatedContacts,
          ],
          redeemEmail: item.redeemEmail
            ? item.redeemEmail
            : type === REDEEM_EMAIL || type === REDEEM_EMAIL_AND_PHONE,
          redeemPhone: item.redeemPhone
            ? item.redeemPhone
            : type === REDEEM_PHONE || type === REDEEM_EMAIL_AND_PHONE,
        };
      }

      return item;
    });
  },
  updateMultipleSequence(state, { type, sequenceInfo, peopleList }) {
    state.lists = state.lists.map((item) => {
      if (peopleList.includes(item.people_search_id)) {
        let sequences = item.sequences || [];
        if (type === "add") {
          const alreadyHasSequenceItem = sequences.find(
            (child) =>
              child.sequence_identifier === sequenceInfo.sequence_identifier,
          );
          if (!alreadyHasSequenceItem) {
            sequences = [...sequences, sequenceInfo];
          }
        } else {
          sequences = sequences.filter(
            (child) =>
              child.sequence_identifier !== sequenceInfo.sequence_identifier,
          );
        }
        return { ...item, sequences };
      }
      return item;
    });
  },
};

const actions = {
  /*----------------【重构】筛选器相关的 Actions-----------------*/
  async searchPeopleList({ commit, state, getters }) {
    if (getIsAccessDisabled(state.people_search_list_access_code)) {
      return;
    }
    const requestId = state.currentRequestId + 1;
    commit("setRequestId", requestId);

    if (state.searchAbortController) {
      state.searchAbortController.abort();
    }
    const controller = new AbortController();
    commit("setSearchAbortController", controller);

    const apiPayload = getters.apiPayload;

    const paramsEmpty = (params) => {
      let allEmpty = true;
      Object.keys(params).forEach((item) => {
        if (
          [
            "page",
            "per_page",
            "search_modes",
            "hash",
            "filter_conditions",
          ].includes(item)
        )
          return;
        if (item === "contact_details") {
          if (isContactDetailsActive(params[item])) allEmpty = false;
          return;
        }
        if (
          (Array.isArray(params[item]) && params[item].length > 0) ||
          (typeof params[item] === "string" && !!params[item]) ||
          (params[item] instanceof Map && params[item].size > 0)
        ) {
          allEmpty = false;
        }
      });
      return allEmpty;
    };

    if (paramsEmpty(apiPayload)) {
      commit("setSearchPeopleList", {
        data: { companies: [], pagination: {} },
      });
      router.push({ name: "people" });
      return;
    }

    try {
      commit("setLoading", true);
      const result = await people.searchPeopleList(
        apiPayload,
        controller.signal,
      );
      if (result && result.data && state.currentRequestId === requestId) {
        commit("setSearchPeopleList", result);
        if (apiPayload.search_modes.includes("real_time")) {
          commit("setRealTimeMode", true);
        }
        result?.metadata?.recent_search_log_identifier &&
          router.push({
            name: "people",
            query: {
              recent_search_log_identifier:
                result?.metadata?.recent_search_log_identifier,
            },
          });
        return result.data;
      }
    } catch (error) {
      const code = error?.response?.data?.code;

      commit("set_people_search_list_access_code", +code);
      if (error.code === "ERR_CANCELED") {
      }
    } finally {
      if (state.currentRequestId === requestId) {
        commit("setLoading", false);
      }
    }
  },

  updateFilter({ commit }, payload) {
    commit("UPDATE_FILTER", payload);
  },

  clearFilterGroup({ commit }, dataKey) {
    if (
      [
        "industry",
        "technology",
        "companyAndPeopleLocations",
        "news",
        "jobs",
        "advertisement",
        "places",
      ].includes(dataKey)
    ) {
      commit("CLEAR_COMPOSITE_FILTER", dataKey);
    } else {
      const initial = getInitialFilterState();
      commit("UPDATE_FILTER", { dataKey, value: initial[dataKey] });
      const excludeKey = `exclude_${dataKey}`;
      if (Object.prototype.hasOwnProperty.call(initial, excludeKey)) {
        commit("UPDATE_FILTER", { dataKey: excludeKey, value: initial[excludeKey] });
      }
    }
  },
  clearAllFilters({ commit }) {
    commit("RESET_ALL_FILTERS");
  },

  /*---------------- 原有的、与筛选器无关的 Actions 保持不变 -----------------*/
  /*-------------人员表格请求Email地址----------------*/
  async getEmail(context, val) {
    try {
      const result = await people.getEmail({
        ...val,
        profile_id: this.state.user.currentId.profile_id,
      });
      return result?.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  },
  /*-------------delete item from group----------------*/
  async deletePeople(context, val) {
    try {
      const result = await people.deletePeople({
        ...val,
        profile_id: this.state.user.currentId.profile_id,
      });
      return result;
    } catch (err) {
      console.log(err);
      throw err;
    }
  },
  /*-----根据groups返回domain ID------*/
  async getDomainIdByGroup(context, data) {
    try {
      const result = await people.getDomainIdByGroup({
        ...data,
        profile_id: this.state.user.currentId.profile_id,
      });
      return result?.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  },
  /*---------------export------------------*/
  async export(context, data) {
    try {
      const result = await people.exportCompany({
        ...data,
        profile_id: this.state.user.currentId.profile_id,
      });
      return result;
    } catch (err) {
      console.log(err);
      throw err;
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters, // 【重构】导出 getters
  actions,
};
